import { ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { TaskLocation, TaskLocations } from '@/api';
import { useWebsocket } from '@/common/providers/websocket-provider';
import {
    FETCH_TASK_LOCATIONS_QUERY_KEY,
    useFetchTaskLocations
} from '../../queries';
import { TaskLocationsContext } from './context';

type V2LocationModel = {
    coordinates: number[];
    altitude: number | null;
    speed: number | null;
    accuracy: number | null;
    timestamp: number;
    what3words: string | null;
    userId: string;
    taskId: string;
    teamId: string;
    companyId: string;
};

type TaskLocationsProviderProps = {
    readonly taskId: string;
    readonly companyId: string;
    readonly children?: ReactNode;
};
export function TaskLocationsProvider(
    props: TaskLocationsProviderProps
): ReactElement {
    const { taskId, companyId, children } = props;

    const { locations, status, refetch } = useFetchTaskLocations(taskId);

    const { subscribe, unsubscribe, on, off } = useWebsocket();
    const queryClient = useQueryClient();

    useEffect(() => {
        const ids = subscribe([
            { resource: `companies#${companyId}.tasks_locations` }
        ]);
        return () => {
            unsubscribe(ids);
        };
    }, [companyId, subscribe, unsubscribe]);

    useEffect(() => {
        const locationsCallback = (message: unknown) => {
            const { data } = message as {
                data: { locations: V2LocationModel[] };
            };

            if (data.locations.length === 0) {
                return;
            }

            if (taskId !== data.locations[0].taskId) {
                return;
            }

            updateLocationsCache(queryClient, taskId, data.locations);
        };
        on('tasks_locations', locationsCallback);

        return () => {
            off('tasks_locations', locationsCallback);
        };
    }, [on, off, taskId, queryClient]);

    const latestLocation = useMemo(() => {
        if (locations.length === 0) return null;
        return locations[0];
    }, [locations]);

    return (
        <TaskLocationsContext.Provider
            value={{
                locations,
                latestLocation,
                status,
                refetch
            }}
        >
            {children}
        </TaskLocationsContext.Provider>
    );
}

/**
 * Splices pushed locations into the cached pages of `useFetchTaskLocations`.
 *
 * A cache write, so `queries/tasks-cache` is where it belongs; it is still
 * here because it takes the socket's own location model, and moving it means
 * moving the subscription above with it into a `useEnableTaskLocationsSockets`
 * beside the other socket hooks. That is a separate change.
 */
function updateLocationsCache(
    queryClient: QueryClient,
    taskId: string,
    locations: V2LocationModel[]
) {
    if (locations.length === 0) {
        return;
    }

    queryClient
        .getQueriesData({
            queryKey: [FETCH_TASK_LOCATIONS_QUERY_KEY, taskId],
            exact: true
        })
        .forEach((queryData) => {
            queryClient.setQueryData(queryData[0], (__prevData: unknown) => {
                const prevData =
                    (__prevData as {
                        pages?: TaskLocations[];
                    } | null) || undefined;

                if (
                    !prevData ||
                    !prevData.pages ||
                    prevData.pages.length === 0
                ) {
                    return prevData;
                }

                const newData = {
                    ...prevData,
                    pages: [...prevData.pages]
                };

                for (const _newLocation of locations) {
                    const newLocation: TaskLocation = {
                        lat: _newLocation.coordinates[0],
                        lng: _newLocation.coordinates[1],
                        alt: _newLocation.altitude ?? undefined,
                        acc: _newLocation.accuracy ?? undefined,
                        speed: _newLocation.speed ?? undefined,
                        ts: new Date(_newLocation.timestamp).toISOString()
                    };

                    // Find slot to insert new location
                    for (const page of newData.pages) {
                        if (page.data.length === 0) {
                            page.data.push(newLocation);
                            break;
                        }

                        // Get first and last location in page
                        const firstLocation = page.data[0];
                        const lastLocation = page.data[page.data.length - 1];

                        // If new location is newer than the first location, insert at the beginning
                        const isNewLocationNewerThanFirstLocation =
                            newLocation.ts > firstLocation.ts;
                        if (isNewLocationNewerThanFirstLocation) {
                            page.data.unshift(newLocation);
                            break;
                        }

                        // If new location is older than the last location, and there is not a next page,
                        // insert at the end, otherwise continue to the next page
                        const isNewLocationOlderThanLastLocation =
                            newLocation.ts < lastLocation.ts;
                        if (isNewLocationOlderThanLastLocation) {
                            // Check if there's a next page
                            const hasNextPage = page.nextTs !== null;
                            if (!hasNextPage) {
                                page.data.push(newLocation);
                                break;
                            } else {
                                continue;
                            }
                        }

                        // If new location is between the first and last location, insert in the middle using binary search
                        const isNewLocationBetweenFirstAndLastLocation =
                            newLocation.ts > lastLocation.ts &&
                            newLocation.ts < firstLocation.ts;
                        if (isNewLocationBetweenFirstAndLastLocation) {
                            let left = 0;
                            let right = page.data.length - 1;

                            while (left <= right) {
                                const mid = (left + right) >> 1; // Same as Math.floor((left + right) / 2)

                                if (page.data[mid].ts === newLocation.ts) {
                                    // If it's exactly the same timestamp, we'll insert after mid
                                    left = mid + 1;
                                    break;
                                } else if (page.data[mid].ts < newLocation.ts) {
                                    // new location is more recent than page.data[mid].ts, so we go left
                                    right = mid - 1;
                                } else {
                                    // new location is "older" than page.data[mid].ts, so we go right
                                    left = mid + 1;
                                }
                            }

                            // Insert at the found position
                            page.data.splice(left, 0, newLocation);
                        }
                    }
                }

                return newData;
            });
        });
}
